import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle, Clock, AlertTriangle, User, Calendar, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FollowUpsPage = () => {
  const { user, chapter } = useAuth();
  const navigate = useNavigate();
  const [followups, setFollowups] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAssigned, setFilterAssigned] = useState("");

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";
  const canEdit = user?.role === "admin" || user?.role === "host";

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterAssigned]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      let url = `${API}/followups?`;
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterAssigned) url += `assigned_to=${filterAssigned}&`;

      const [fuRes, visitorsRes, membersRes] = await Promise.all([
        fetch(url, { headers }),
        fetch(`${API}/visitors`, { headers }),
        fetch(`${API}/members`, { headers })
      ]);

      if (fuRes.ok) setFollowups(await fuRes.json());
      if (visitorsRes.ok) setVisitors(await visitorsRes.json());
      if (membersRes.ok) setMembers(await membersRes.json());
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (followupId, status) => {
    try {
      const response = await fetch(`${API}/followups/${followupId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success("Status updated");
        fetchData();
      }
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const getVisitorName = (id) => visitors.find(v => v.visitor_id === id)?.full_name || "Unknown";
  const getMemberName = (id) => members.find(m => m.member_id === id)?.full_name || "Unknown";

  const getStatusIcon = (status) => {
    switch(status) {
      case "Done": return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "Overdue": return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case "In Progress": return <Clock className="h-4 w-4 text-yellow-600" />;
      default: return <Clock className="h-4 w-4 text-blue-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case "Done": return "bg-green-100 text-green-700";
      case "Overdue": return "bg-red-100 text-red-700";
      case "In Progress": return "bg-yellow-100 text-yellow-700";
      default: return "bg-blue-100 text-blue-700";
    }
  };

  const getTaskTypeColor = (type) => {
    switch(type) {
      case "Close": return "bg-green-100 text-green-700";
      case "Influence": return "bg-yellow-100 text-yellow-700";
      default: return "bg-purple-100 text-purple-700";
    }
  };

  // Group by status
  const overdueFollowups = followups.filter(f => f.status === "Overdue");
  const openFollowups = followups.filter(f => f.status === "Open");
  const inProgressFollowups = followups.filter(f => f.status === "In Progress");
  const doneFollowups = followups.filter(f => f.status === "Done");

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div></Layout>;
  }

  const FollowupCard = ({ fu, index }) => (
    <Card 
      key={fu.followup_id}
      className="border-0 shadow-sm hover:shadow-md transition-shadow animate-fade-in"
      style={{ animationDelay: `${index * 0.03}s` }}
      data-testid={`followup-card-${fu.followup_id}`}
    >
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {getStatusIcon(fu.status)}
              <Badge className={getTaskTypeColor(fu.task_type)}>{fu.task_type}</Badge>
              {fu.interest_level && (
                <Badge variant="outline">Level {fu.interest_level}</Badge>
              )}
            </div>
            
            <button 
              onClick={() => {
                const visitor = visitors.find(v => v.visitor_id === fu.visitor_id);
                if (visitor) navigate(`/visitors/${visitor.visitor_id}`);
              }}
              className="text-left hover:underline"
            >
              <p className="font-semibold text-slate-900">{getVisitorName(fu.visitor_id)}</p>
            </button>
            
            <div className="mt-2 space-y-1 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="h-3.5 w-3.5" />
                <span>Assigned to: <span className="text-slate-700">{getMemberName(fu.assigned_to_member_id)}</span></span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span>Due: <span className={fu.status === "Overdue" ? "text-red-600 font-medium" : "text-slate-700"}>{fu.due_date}</span></span>
              </div>
            </div>

            {fu.notes && (
              <p className="mt-2 text-sm text-slate-600 line-clamp-2">{fu.notes}</p>
            )}
          </div>

          {canEdit && fu.status !== "Done" && (
            <Select 
              value={fu.status} 
              onValueChange={(v) => handleStatusChange(fu.followup_id, v)}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Done">Done</SelectItem>
              </SelectContent>
            </Select>
          )}

          {fu.status === "Done" && (
            <Badge className="bg-green-100 text-green-700">Completed</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="followups-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">Follow-ups</h1>
            <p className="text-muted-foreground mt-1">
              {overdueFollowups.length > 0 && (
                <span className="text-red-600 font-medium">{overdueFollowups.length} overdue</span>
              )}
              {overdueFollowups.length > 0 && openFollowups.length > 0 && " • "}
              {openFollowups.length} open tasks
            </p>
          </div>

          <div className="flex gap-3">
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Done">Done</SelectItem>
                <SelectItem value="Overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterAssigned || "all"} onValueChange={(v) => setFilterAssigned(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Members" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Members</SelectItem>
                {members.map(m => (
                  <SelectItem key={m.member_id} value={m.member_id}>{m.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue</p>
                  <p className="text-2xl font-bold text-red-600">{overdueFollowups.length}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Open</p>
                  <p className="text-2xl font-bold text-blue-600">{openFollowups.length}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                  <p className="text-2xl font-bold text-yellow-600">{inProgressFollowups.length}</p>
                </div>
                <ArrowRight className="h-8 w-8 text-yellow-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{doneFollowups.length}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Overdue Section */}
        {overdueFollowups.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Overdue ({overdueFollowups.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {overdueFollowups.map((fu, index) => <FollowupCard key={fu.followup_id} fu={fu} index={index} />)}
            </div>
          </div>
        )}

        {/* Open Section */}
        {openFollowups.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Open Tasks ({openFollowups.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {openFollowups.map((fu, index) => <FollowupCard key={fu.followup_id} fu={fu} index={index} />)}
            </div>
          </div>
        )}

        {/* In Progress Section */}
        {inProgressFollowups.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">In Progress ({inProgressFollowups.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {inProgressFollowups.map((fu, index) => <FollowupCard key={fu.followup_id} fu={fu} index={index} />)}
            </div>
          </div>
        )}

        {/* Completed Section */}
        {doneFollowups.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 opacity-60">Completed ({doneFollowups.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-60">
              {doneFollowups.slice(0, 6).map((fu, index) => <FollowupCard key={fu.followup_id} fu={fu} index={index} />)}
            </div>
          </div>
        )}

        {followups.length === 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center text-muted-foreground">
              No follow-ups yet. Create follow-ups from visitor profiles when setting interest levels.
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default FollowUpsPage;
